/**
 * components/legal/priceListCopy.ts
 *
 * The words of the price list (5 Oct 2026), Czech and English side by side:
 * everything `pricelist/PriceListPlans.tsx` prints on the welcome page. The
 * contract is counsel's price list at /subscription (`vop/vopPriceList.ts`),
 * and every line here has to agree with it.
 *
 * ## Why the copy lives here and not in `i18n/`
 *
 * It is the price list, which is legal text: the complaints procedure
 * warrants that the service matches the description at /subscription, so
 * every line below is a promise, revised as one. It sits beside the documents
 * (`legalContent.ts`) for the reason they sit outside `en.ts` / `cs.ts`, and
 * `priceListCopy.test.ts` holds the two languages to one shape, as the
 * `Translations` type would have.
 *
 * ## What it may say
 *
 * - **No amount is typed.** Every figure is built from `priceList.ts`, the
 *   mirror `priceList.parity.test.ts` holds to the billing engine, so the
 *   page cannot state a price the checkout does not charge. The test fails a
 *   line that prints a number the price list does not hold.
 * - **Only what the product keeps.** No line says AI, 24/7 or "world
 *   languages" (support answers in Czech and English), and none names where
 *   market data is read. The cadence is always "up to": it is a ceiling,
 *   support sets it for each property and nothing enforces it.
 * - **The product follows the property.** Nobody picks one, so the copy says
 *   who each price is for and never "choose". OPTIVO Rentals is for a
 *   guesthouse, an apartment, a vacation rental or a campsite of any size (the owner set
 *   no limit, 5 Oct 2026), so no line speaks of one; and it is a "pronájem",
 *   not a private person's: the terms sell to businesses.
 * - **A price is two figures, and the page adds them up once.** A property
 *   pays its product's own price and a price for each room, so each product
 *   works an example month out from the two ({@link EXAMPLE_ROOMS}). What a
 *   room is — for a rental, the unit let — is said once, in the small print.
 * - **The euro figure is a guide.** Billing is CZK-only; the small print says
 *   so beside the rate the guide is worked out at.
 * - **No discount.** Optivo gives none since 5 Oct 2026: a product states its
 *   monthly price and nothing off it. The list before printed a year's price
 *   under each product, a month cheaper than twelve, and "12 months for the
 *   price of 11" in the small print. A year is twelve months at the monthly
 *   price, which needs no figure of its own; the month a customer does not
 *   pay for is the trial, which the page says where it offers sign-up.
 */

import { COMPANY } from "./companyDetails";
import {
    EUR_GUIDE_CZK_PER_EUR,
    PRICE_LIST_PLANS,
    PRICE_LIST_PLAN_NAMES,
    RENTALS_PROPERTY_TYPES,
    eurGuide,
    type PriceListPlan,
    type PriceListPlanKey,
} from "./priceList";

export type PriceListLanguage = "cs" | "en";

export interface PriceListProductCopy {
    key: PriceListPlanKey;
    /** The product's own name, the same in both languages. */
    name: string;
    /** Who it is for, in a few words: the kicker over the name. */
    kicker: string;
    /**
     * One property for one month, before its rooms. The figure and its
     * currency are apart because the page sets the figure large and the
     * currency beside it.
     */
    price: string;
    currency: string;
    /** What that price is charged by. */
    unit: string;
    /** What each room adds to it, as one line: "+ 30 Kč za každý pokoj měsíčně". */
    roomPrice: string;
    /** Both prices in euros, a guide only. */
    eurGuide: string;
    /** Which properties pay these prices, in a sentence. */
    audience: string;
    /** The two prices added up for one property, so the reader does not have to. */
    example: string;
}

export interface PriceListCopy {
    /** In the price list's own order. */
    products: PriceListProductCopy[];
    includedTitle: string;
    /** What both products include, one line to a tick. */
    included: string[];
    agreementTitle: string;
    /** What has no list price, and where to ask for one. */
    agreement: string;
    smallPrint: string[];
}

/**
 * Built rather than typed: an escape written into a source file has reached
 * this repository as the character itself before, invisible in every diff.
 */
const NO_BREAK_SPACE = String.fromCharCode(0xa0);

/** Digits in threes: "2 990" as Czech writes it, "2,990" as English does. */
function figure(lang: PriceListLanguage, value: number): string {
    return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, lang === "cs" ? NO_BREAK_SPACE : ",");
}

const CURRENCY: Record<PriceListLanguage, string> = { cs: "Kč", en: "CZK" };

/** An amount with its currency, the two joined so a line never ends between them. */
function amount(lang: PriceListLanguage, czk: number): string {
    return `${figure(lang, czk)}${NO_BREAK_SPACE}${CURRENCY[lang]}`;
}

const capitalised = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * What both products include, as numbers. The lesser of the two wherever they
 * differ, so a line headed "included in both" stays true of each.
 */
export function includedInBoth(): Pick<PriceListPlan, "uploadsPerDay" | "horizonDays" | "analysesPerQuarter"> {
    const least = (field: "uploadsPerDay" | "horizonDays" | "analysesPerQuarter"): number =>
        Math.min(...PRICE_LIST_PLANS.map((plan) => plan[field]));
    return {
        uploadsPerDay: least("uploadsPerDay"),
        horizonDays: least("horizonDays"),
        analysesPerQuarter: least("analysesPerQuarter"),
    };
}

/**
 * How each property type OPTIVO Rentals is for is named in a sentence. Keyed
 * by the price list's own types, so one joining them does not compile until
 * it has a name here.
 */
const RENTAL_KINDS: Record<(typeof RENTALS_PROPERTY_TYPES)[number], Record<PriceListLanguage, string>> = {
    pension_bb: { cs: "penzion", en: "a guesthouse" },
    apartment: { cs: "apartmán", en: "an apartment" },
    villa_vacation_rental: { cs: "rekreační pronájem", en: "a vacation rental" },
    campsite: { cs: "kemp", en: "a campsite" },
};

/** "a, b nebo c": the last two joined by the word, the rest by commas. */
function oneOf(lang: PriceListLanguage, items: readonly string[]): string {
    if (items.length < 2) return items.join("");
    const or = lang === "cs" ? " nebo " : " or ";
    return `${items.slice(0, -1).join(", ")}${or}${items[items.length - 1]}`;
}

/** "penzion, apartmán, rekreační pronájem nebo kemp" — the properties OPTIVO Rentals is for. */
export function rentalKinds(lang: PriceListLanguage): string {
    return oneOf(
        lang,
        RENTALS_PROPERTY_TYPES.map((type) => RENTAL_KINDS[type][lang]),
    );
}

/**
 * The property each product's example is worked out for: a hotel of twenty
 * rooms, a guesthouse of ten. Counts of rooms, not prices: the month they
 * come to is built from the price list like every other amount here.
 */
export const EXAMPLE_ROOMS: Record<PriceListPlanKey, number> = { pro: 20, rentals: 10 };

/** What a month comes to for a property of `rooms` rooms on `plan`, in CZK. */
export function exampleMonthCzk(plan: PriceListPlan, rooms: number): number {
    return plan.propertyMonthlyCzk + rooms * plan.roomMonthlyCzk;
}

/** "jednou za čtvrtletí" — in a word while the list includes one, in figures otherwise. */
function perQuarter(lang: PriceListLanguage, count: number): string {
    if (lang === "cs") return count === 1 ? "jednou za čtvrtletí" : `${count}× za čtvrtletí`;
    return count === 1 ? "once a quarter" : `${count}× a quarter`;
}

/** What starts being agreed individually: more analyses than the list includes. */
function beyondIncludedAnalyses(lang: PriceListLanguage, count: number): string {
    if (lang === "cs") {
        return count === 1
            ? "Více než jedna analýza strategie za čtvrtletí"
            : `Více analýz strategie za čtvrtletí než ${count}`;
    }
    return count === 1 ? "More than one revenue study a quarter" : `More than ${count} revenue studies a quarter`;
}

const KICKERS: Record<PriceListPlanKey, Record<PriceListLanguage, string>> = {
    pro: { cs: "Pro hotely", en: "For hotels" },
    rentals: { cs: "Pro penziony a pronájmy", en: "For guesthouses and rentals" },
};

/**
 * Which properties pay a product's prices. The type alone decides, so the
 * rental product says that size does not, and neither states a limit.
 */
function audience(lang: PriceListLanguage, key: PriceListPlanKey): string {
    if (key === "pro") {
        return lang === "cs"
            ? "Každý objekt, který není penzionem ani pronájmem: hotel, resort i hostel."
            : "Every property that is not a guesthouse or a rental: a hotel, a resort or a hostel.";
    }
    const kinds = capitalised(rentalKinds(lang));
    return lang === "cs" ? `${kinds}, s jakýmkoli počtem pokojů.` : `${kinds}, with any number of rooms.`;
}

/**
 * An amount in euros as the guide states it: whole from ten euros up, to the
 * cent below (`eurGuide`), with the decimal mark of the language.
 */
function euros(lang: PriceListLanguage, czk: number): string {
    const value = eurGuide(czk);
    const text = Number.isInteger(value) ? figure(lang, value) : value.toFixed(2).replace(".", lang === "cs" ? "," : ".");
    return lang === "cs" ? `${text}${NO_BREAK_SPACE}€` : `€${text}`;
}

/** The property an example is worked out for, as the sentence names it with its rooms. */
const EXAMPLE_PROPERTY: Record<PriceListPlanKey, Record<PriceListLanguage, string>> = {
    pro: { cs: "hotel s {rooms} pokoji", en: "a hotel with {rooms} rooms" },
    rentals: { cs: "penzion s {rooms} pokoji", en: "a guesthouse with {rooms} rooms" },
};

function example(lang: PriceListLanguage, plan: PriceListPlan): string {
    const rooms = EXAMPLE_ROOMS[plan.key];
    const property = EXAMPLE_PROPERTY[plan.key][lang].replace("{rooms}", String(rooms));
    const month = amount(lang, exampleMonthCzk(plan, rooms));
    return lang === "cs" ? `Například ${property}: ${month} měsíčně.` : `For example, ${property}: ${month} a month.`;
}

function productCopy(lang: PriceListLanguage, plan: PriceListPlan): PriceListProductCopy {
    const room = `+${NO_BREAK_SPACE}${amount(lang, plan.roomMonthlyCzk)}`;
    const guide = `≈${NO_BREAK_SPACE}${euros(lang, plan.propertyMonthlyCzk)} + ${euros(lang, plan.roomMonthlyCzk)}`;
    return {
        key: plan.key,
        name: PRICE_LIST_PLAN_NAMES[plan.key],
        kicker: KICKERS[plan.key][lang],
        price: figure(lang, plan.propertyMonthlyCzk),
        currency: CURRENCY[lang],
        unit: lang === "cs" ? "za objekt měsíčně" : "per property per month",
        roomPrice: lang === "cs" ? `${room} za každý pokoj měsíčně` : `${room} per room per month`,
        eurGuide: lang === "cs" ? `${guide} za pokoj` : `${guide} per room`,
        audience: audience(lang, plan.key),
        example: example(lang, plan),
    };
}

function buildCopy(lang: PriceListLanguage): PriceListCopy {
    const { uploadsPerDay, horizonDays, analysesPerQuarter } = includedInBoth();
    const products = PRICE_LIST_PLANS.map((plan) => productCopy(lang, plan));
    const rate = amount(lang, EUR_GUIDE_CZK_PER_EUR);

    if (lang === "cs") {
        return {
            products,
            includedTitle: "V ceně obou produktů",
            included: [
                `Ceny na každou noc aktualizované až ${uploadsPerDay}× denně, na ${horizonDays} dní dopředu`,
                "Napojení na váš hotelový systém (PMS): denní načítání rezervací a zápis cen zpět",
                "Tržní data (ceny konkurence) a počasí",
                "Prognózy a analytika",
                "Navázané cenové plány tam, kde je váš hotelový systém má",
                "Neomezený počet uživatelů",
                `Analýza strategie s doporučeným nastavením, ${perQuarter("cs", analysesPerQuarter)}`,
                "Podpora v češtině a angličtině",
            ],
            agreementTitle: "Po dohodě",
            agreement: `${beyondIncludedAnalyses("cs", analysesPerQuarter)} nebo rozšířená tržní data: individuální cena. Napište nám na ${COMPANY.email}.`,
            smallPrint: [
                "Pokoj je každý pokoj nebo ubytovací jednotka (například apartmán), pro kterou Optivo nastavuje ceny.",
                "Bez zřizovacího poplatku. Měsíční předplatné je bez závazku.",
                "Účtujeme v českých korunách (CZK). Poskytovatel není plátcem DPH, uvedené ceny jsou konečné.",
                `Částky v eurech jsou orientační, přepočtené pevným kurzem ${rate} za euro.`,
            ],
        };
    }
    return {
        products,
        includedTitle: "Included in both products",
        included: [
            `Nightly prices updated up to ${uploadsPerDay}× a day, ${horizonDays} days ahead`,
            "Connection to your property management system (PMS): bookings read daily, prices written back",
            "Market data (competitor prices) and weather",
            "Forecasts and analytics",
            "Linked rate plans, where your PMS has them",
            "Unlimited users",
            `A revenue study with recommended settings, ${perQuarter("en", analysesPerQuarter)}`,
            "Support in Czech and English",
        ],
        agreementTitle: "By agreement",
        agreement: `${beyondIncludedAnalyses("en", analysesPerQuarter)}, or extended market data: an individual price. Write to us at ${COMPANY.email}.`,
        smallPrint: [
            "A room is every room or accommodation unit (an apartment, for example) that Optivo sets prices for.",
            "No setup fee. A monthly subscription carries no commitment.",
            "Billing is in CZK. The provider is not registered for VAT, so the prices stated are final.",
            `The euro figures are a guide only, converted at a fixed ${rate} to the euro.`,
        ],
    };
}

const COPY: Record<PriceListLanguage, PriceListCopy> = { cs: buildCopy("cs"), en: buildCopy("en") };

/** The price list's words in `lang`. The same object every time, so a page may hold it as a prop. */
export function priceListCopy(lang: PriceListLanguage): PriceListCopy {
    return COPY[lang];
}

/**
 * Every line the price list prints, in reading order, a price beside its
 * currency as the page sets them. The copy is an object where the documents
 * are plain strings, so whatever asks "does the page say this?" — the
 * documents' tests among them — reads it through here and misses nothing.
 */
export function priceListCopyLines(copy: PriceListCopy): string[] {
    return [
        ...copy.products.flatMap((product) => [
            product.kicker,
            product.name,
            `${product.price}${NO_BREAK_SPACE}${product.currency}`,
            product.unit,
            product.roomPrice,
            product.eurGuide,
            product.audience,
            product.example,
        ]),
        copy.includedTitle,
        ...copy.included,
        copy.agreementTitle,
        copy.agreement,
        ...copy.smallPrint,
    ];
}
